/**
 * Whether this browser is inside a support session, for code that cannot use
 * a React hook.
 *
 * A support session authenticates as the target student, so every telemetry
 * helper that reads `useAuthStore.user` identifies the *student* — meaning the
 * operator's clicks, timings and lesson events are recorded as that child's
 * behaviour and distort product analytics and experiment arms.
 *
 * A module-level flag rather than auth-store state on purpose: the store
 * resets its token in six places, and a marker that has to stay in step with
 * all of them is one that eventually will not.
 *
 * Four writers, all genuine auth transitions: `syncSession` sets it and its
 * no-user branch clears it, `clearAuth` clears it, and the two credential
 * sign-ins (`page/Login.tsx`, `hooks/useAuth.ts`) clear it. Deliberately not
 * `setAuth` or `persistAuth` — both are shared setters also used for a name
 * refresh and for config hydration, and writing the marker there cleared it
 * mid-session. Every reader just asks.
 */
const STORAGE_KEY = "support_actor_id";

/**
 * Seeded from storage at module load, not only by `syncSession`.
 *
 * On a reload the auth store can hydrate `user` from localStorage and fire
 * `trackSessionStart` before `syncSession` has confirmed the session — so a
 * marker set only by `syncSession` would let one event through attributed to
 * the student. Reading it here closes that window.
 */
const readStored = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    // Private mode, blocked storage: fall back to "not a support session".
    return null;
  }
};

let supportActorId: string | null = readStored();

/**
 * Record the current support session, as ``actor:sessionKey``.
 *
 * The actor alone is too coarse. When the same operator redeems a second
 * grant while an earlier support tab is open, an actor-only marker writes the
 * identical value -- so the browser emits no `storage` event, that tab never
 * reloads, and it goes on showing the first target while the browser-wide
 * cookie authenticates its requests as the second. Split identity, with the
 * operator acting on the wrong account believing otherwise.
 *
 * The session key is the same grain the backend namespaces runtime sessions
 * by, so the two ends agree on what "a different support session" means.
 * Falls back to the actor alone when no key is available, which degrades to
 * the coarse behaviour rather than failing.
 */
export const setSupportSession = (
  actorId: string | null,
  sessionKey?: string | null,
): void => {
  const marker = actorId
    ? sessionKey
      ? `${actorId}:${sessionKey}`
      : actorId
    : null;
  supportActorId = marker;
  try {
    if (supportActorId) {
      window.localStorage.setItem(STORAGE_KEY, supportActorId);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // In-memory value still holds for this page.
  }
};

/** The operator's id, when this browser holds a support session. */
export const getSupportActor = (): string | null =>
  supportActorId ? supportActorId.split(":")[0] : null;

/** The full marker, including the per-session discriminator. */
export const getSupportMarker = (): string | null => supportActorId;

/** True when telemetry and identification must be suppressed. */
export const isSupportSession = (): boolean => supportActorId !== null;

/**
 * Keep other tabs honest.
 *
 * The session cookie is browser-wide, so redeeming a support link in one tab
 * silently changes what every other open tab is authenticated as. Those tabs
 * keep their own module variable — so they show no banner while their API
 * calls run as the target user, which is the single most dangerous state this
 * feature can be in.
 *
 * `storage` fires only in the *other* tabs, which is exactly the audience.
 * Reloading rather than patching state in place, for the same reason the
 * redeem page reloads on success: it is the only way to guarantee every
 * consumer — the banner's `useSession` included — restarts from the real
 * cookie. Guarded on an actual change so it cannot loop.
 */
if (
  typeof window !== "undefined" &&
  typeof window.addEventListener === "function"
) {
  window.addEventListener("storage", (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY) return;
    const incoming = event.newValue || null;
    if (incoming === supportActorId) return;
    supportActorId = incoming;
    window.location.reload();
  });
}
